import classNames from 'classnames';
import Marquee from 'react-fast-marquee';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import Breadcrumbs, { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import Button from '@/shared/components/shared/Button';
import Image from '@/shared/components/shared/Image';
import Video from '@/shared/components/shared/Video';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { ImageShape, VideoShape } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { scrollToAnchor } from '@/shared/lib/utils/scrollToAnchor';

export type ExpoHeroSectionRawProps = {
    media?: ImageShape | VideoShape | null;
    title?: string | null;
    text?: string | null;
    date?: string | null;
    location?: string | null;
    programButtonText?: string | null;
    participateButtonText?: string | null;
    participateButtonHref?: string | null;
    partnersTitle?: string | null;
    partners?: ImageShape[] | null;
    breadcrumbs?: AppBreadcrumbs;
};

type Props = React.HTMLAttributes<HTMLElement> & {
    expoHeroSectionProps: ExpoHeroSectionRawProps;
};

const isVideoShape = (media: ImageShape | VideoShape): media is VideoShape => Array.isArray(media);

function ExpoHeroSection({ expoHeroSectionProps, className, ...props }: Props) {
    const {
        media,
        title,
        text,
        date,
        location,
        programButtonText,
        participateButtonText,
        participateButtonHref,
        partnersTitle,
        partners,
        breadcrumbs,
    } = expoHeroSectionProps;
    const device = useMediaQueryDeviceStateValue();
    const tpTitle = useTypograf(title ?? '');
    const tpDate = useTypograf(date ?? '');
    const tpLocation = useTypograf(location ?? '');
    const tpProgramButtonText = useTypograf(programButtonText ?? '');
    const tpParticipateButtonText = useTypograf(participateButtonText ?? '');
    const participateHref = participateButtonHref ?? '#participantsList';
    const participateAnchorId = participateHref.replace(/^#/, '');
    const hasPartners = (partners?.length ?? 0) > 0;
    const hasActions = Boolean(tpProgramButtonText || tpParticipateButtonText);
    const videoSources = media != null && isVideoShape(media) && media.length > 0 ? media : null;
    const imageMedia = media != null && !isVideoShape(media) && media.src ? media : null;

    const handleAnchorClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
        event.preventDefault();
        scrollToAnchor(id);
    };

    return (
        <section {...props} className={classNames('expo-hero-section breadcrumb-dark-bg', className)}>
            <div className="expo-hero-section__main js-header-theme-trigger" data-theme="light">
                {(videoSources || imageMedia) && (
                    <div className="expo-hero-section__media">
                        {videoSources ? (
                            <Video
                                sources={videoSources}
                                className="expo-hero-section__video"
                                autoPlay
                                muted
                                playsInline
                                loop
                                preload="metadata"
                            />
                        ) : (
                            imageMedia && (
                                <Image
                                    className="expo-hero-section__image"
                                    src={imageMedia.src}
                                    alt={imageMedia.alt || ''}
                                    title={imageMedia.title}
                                    width={imageMedia.width}
                                    height={imageMedia.height}
                                    sizes="100vw"
                                    priority={imageMedia.priority}
                                />
                            )
                        )}
                    </div>
                )}

                <div className="wrapper expo-hero-section__content">
                    {breadcrumbs && breadcrumbs.length > 0 && (
                        <Breadcrumbs className="expo-hero-section__breadcrumbs" breadcrumbs={breadcrumbs} />
                    )}

                    {(tpTitle || text) && (
                        <div className="expo-hero-section__head">
                            {tpTitle && <h1 className="expo-hero-section__title h1">{tpTitle}</h1>}
                            {text && (
                                <div
                                    className="expo-hero-section__text text-l wysiwyg"
                                    dangerouslySetInnerHTML={{ __html: tp(text) }}
                                />
                            )}
                        </div>
                    )}

                    <div className="expo-hero-section__bottom">
                        {(date || location) && (
                            <div className="expo-hero-section__meta">
                                {date && (
                                    <div className="expo-hero-section__meta-item">
                                        <div className="expo-hero-section__meta-label text-m">Дата</div>
                                        <div className="expo-hero-section__meta-value text-xl">{tpDate}</div>
                                    </div>
                                )}
                                {location && (
                                    <div className="expo-hero-section__meta-item">
                                        <div className="expo-hero-section__meta-label text-m">Место</div>
                                        <div className="expo-hero-section__meta-value text-xl">{tpLocation}</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {hasActions && (
                            <div className="expo-hero-section__actions">
                                {tpProgramButtonText && (
                                    <Button
                                        tag="a"
                                        href="#program"
                                        variant="outline-light"
                                        size="lg"
                                        onClick={(event) => handleAnchorClick(event, 'program')}
                                    >
                                        {tpProgramButtonText}
                                    </Button>
                                )}
                                {tpParticipateButtonText && (
                                    <Button
                                        tag="a"
                                        href={participateHref}
                                        variant="primary-filled"
                                        size="lg"
                                        onClick={(event) => handleAnchorClick(event, participateAnchorId)}
                                    >
                                        {tpParticipateButtonText}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {hasPartners && (
                <div className="expo-hero-section__partners js-header-theme-trigger" data-theme="dark">
                    {partnersTitle && <div className="expo-hero-section__partners-title text-l">{partnersTitle}</div>}
                    <Marquee className="expo-hero-section__marquee" pauseOnHover={device === 'desktop'}>
                        {partners!
                            .filter((item) => item.src)
                            .map((item, i) => (
                                <div className="expo-hero-section__partner-card" key={i}>
                                    <Image
                                        className="expo-hero-section__partner-img"
                                        src={item.src}
                                        alt={item.alt || ''}
                                        title={item.title}
                                        width={item.width}
                                        height={item.height}
                                        sizes="200px"
                                        unoptimized={item.src.endsWith('.svg')}
                                    />
                                </div>
                            ))}
                    </Marquee>
                </div>
            )}
        </section>
    );
}

export default ExpoHeroSection;
